<template>
  <div
    class="custom_upload dragger-upload"
    @dragenter="dragOver"
    @dragover="dragOver"
    @dragleave="ondragleave"
    @mouseenter="mouseenter"
    @mouseleave="mouseleave"
  >
    <Drop @drop="dropData">
      <div class="dragger-content">
        <slot></slot>
      </div>
    </Drop>
    <div v-if="isDragOver" :class="['drag-tips', dragTipsClass]">
      <slot name="dragTips"></slot>
      <div class="drag-tips-content">
        <div>{{ dragTipsTxt }}<span v-if="toTipsTxt">至</span></div>
        <div v-if="toTipsTxt">
          【
          <span class="toTipsTxt">
            {{ toTipsTxt }}
          </span>
          】
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, defineProps } from 'vue';
import { message } from 'ant-design-vue';
import { useStore } from 'vuex';
import uploadManager from './methods/uploadManager';
import { HandleUploadStatus, canUploadType, UploadActionUrlConfig } from './methods/uploadConfig';
import EventBus from '@/controller/EventBus';
import traverseFileTree from './dragUploadMethods/traverseFileTree';
import attrAccept from './dragUploadMethods/attr-accept';
import Drop from '../Drop/index.vue';
import { getRandomString } from '../../utils/utils';
import { urlResquest } from '../../services/urlConfig';
import { DefaultColumnKey } from '../../views/home/methods';
import checkResStatus from '@/services/ResConfig';

const props = defineProps({
  accept: {
    // 目前采用的是全部允许但会在上传方法中手动处理不符合的文件（弹taost）
    type: String,
    // default: '.mp3,.lrc',
    default: canUploadType.join(','),
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  columnKey: {
    type: String,
    default: '',
  },
  dragTipsClass: String,
  dragTipsTxt: {
    dragTipsTxt: String,
    default: '松开鼠标，文件立即上传',
  },
  toTipsTxt: String,
});
const emit = defineEmits(['update']);

const isDragOver = ref(false);
const store = useStore();
const operationKey = ref(null);
let beforeFileList = []; // 单次选择的文件列表
let cannotDragUpload = false; // 是否是要忽略的拖拽
// 监听此次拖拽是不是拖拽移动到文件夹（非拖拽上传）
EventBus.setCanDragUpload.addEventLister('cannotDragUpload', value => {
  cannotDragUpload = value;
});
const mouseenterStatus = ref(false);

function mouseenter() {
  mouseenterStatus.value = true;
}
function dragOver() {
  if (cannotDragUpload) return;
  isDragOver.value = true;
}
function drop() {
  // 重置状态保证单次选择只上传一次
  operationKey.value = HandleUploadStatus.Init;
  beforeFileList = []; // 清空已经处理完的数据（上次拖拽选择的数据）
  cannotDragUpload = false;
}
function dragEnd() {
  drop();
  isDragOver.value = false;
  cannotDragUpload = false;
}

function ondragleave() {
  // 如果不做鼠标移出的判断提示遮罩会闪烁，即使设置了pointer-events属性也不行
  if (!mouseenterStatus.value) {
    // 鼠标移出且drag也移出时才置状态
    isDragOver.value = false;
  }
}
function mouseleave() {
  mouseenterStatus.value = false;
  ondragleave();
}

function refresh() {
  store.dispatch('getUserInfo');
  emit('update');
}

function canBeforeUpload() {
  return !operationKey.value || operationKey.value === HandleUploadStatus.Init;
}

async function createColumn(params) {
  const res = await urlResquest.createColumn(
    {
      parentColumnKey: props.columnKey || DefaultColumnKey,
      title: params.columnName,
    },
    { errorToast: true }
  );
  if (checkResStatus.isSuccess(res.code)) {
    refresh();
  }
}
function upload(type, params, fileList) {
  if (fileList.length) {
    uploadManager.beforeUpload({
      fileList,
      type,
      params,
      operationCKey: `upload_${getRandomString()}`, // 本次上传的唯一key标记用来处理排队上传首次创建文件夹的问题
      callback: refresh,
    });
    return;
  }
  createColumn(params);
}

function uploadColumn(columnData) {
  const { hasRemoveColumn, fileList, column } = columnData;
  if (hasRemoveColumn) {
    return;
  }
  const params = {
    columnName: column.name,
  };
  if (props.columnKey) {
    params.parentColumnKey = props.columnKey;
  }
  upload(UploadActionUrlConfig.batchUploadByColumn, params, fileList);
}
function customRequestDrag(treeData) {
  const { fileData } = treeData;
  let { hasErrorFile, hasRemoveColumn } = fileData;
  // 可以上次文件夹时，beforeUpload中fileList为单个file的列表，所以改为当前的上传方式
  if (cannotDragUpload) return;
  if (canBeforeUpload()) {
    if (fileData.fileList.length) {
      upload(UploadActionUrlConfig.batchUploadAudioAndIrc, { columnKey: props.columnKey }, [
        ...fileData.fileList,
      ]);
    }
    if (fileData.columnList.length) {
      fileData.columnList.forEach(column => {
        if (column.hasErrorFile) {
          hasErrorFile = true;
        }
        // 有子文件夹的文件夹不允许上传
        if (column.hasRemoveColumn) {
          hasRemoveColumn = true;
        }
        uploadColumn(column);
      });
    }
    if (hasErrorFile) {
      message.error({ content: '已过滤不符合格式的文件' });
    }
    if (hasRemoveColumn) {
      message.error({ content: '暂不支持上传多级文件夹' });
    }
    operationKey.value = HandleUploadStatus.Handled;
  }
  dragEnd();
}

// beforeUpload和c
function dropData(e) {
  // 是否是要忽略的拖拽
  if (cannotDragUpload) return;
  drop();
  const treeData = {
    hasErrorFile: false, // 是否有不符合格式的文件
    hasDeepColumn: false,
    fileData: { column: {}, deepCloumNum: 0, hasRemoveColumn: false, columnList: [], fileList: [] },
  };
  // 设置traverseFileTree的deepCloumNum节省性能
  traverseFileTree(e.dataTransfer.items, treeData, _file => attrAccept(_file, props.accept), 2);
  setTimeout(() => {
    console.log('zxx--treeData--', treeData, e.dataTransfer.items, props.accept);
    customRequestDrag(treeData);
  }, 300);
}
</script>
<style lang="scss">
.dragger-upload {
  position: relative;
  .ant-upload.ant-upload-drag {
    cursor: auto;
  }
}

.drag-tips {
  pointer-events: none;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.3);
  // left: $navSiderWidth;
  display: flex;
  justify-content: center;
  align-items: center;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  color: #fff;
  z-index: 1000;
  .drag-tips-content {
    flex-direction: column;
    border: 4px dashed $grey3;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    text-align: center;
    font-weight: bold;
    font-size: 24px;
    color: #fff;
    height: 218px;
    line-height: 28px;
    width: 552px;
    .toTipsTxt {
      max-width: 64%;
      vertical-align: middle;
      display: inline-block;
      @include ellipsis;
    }
  }
}
</style>
